import React, {useState, useEffect} from "react"
import {useIoTShadowStore} from "../../store/iotShadowStore"
import {useFavorites} from "../../hooks/useFavorites"
import {DeviceCardProps, NormalizedShadowData} from "../../types/shadow"
import {buildDeviceInfo, getTimeSinceLastUpdate} from "../../utils/versionUtils"
import {useConnectionStatusDisplay} from "../ConnectionStatusDisplay"
import {DeviceCardHeader} from "../DeviceCardHeader"
import {DeviceInfoPanel} from "../DeviceInfoPanel"
import {ShadowSummaryPanel} from "../ShadowSummaryPanel"
import {ShadowFullDataPanel} from "../ShadowFullDataPanel"

export const DeviceCard = ({
	deviceId,
	onRefresh,
	isLiveMode = false,
	connectionStatus,
}: DeviceCardProps) => {
	const device = useIoTShadowStore((state) => state.devices.get(deviceId))
	const [showShadowData, setShowShadowData] = useState(false)
	const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null)
	const {favorites, toggleFavorite} = useFavorites()
	const isFavorite = favorites.includes(deviceId)
	const statusDisplay = useConnectionStatusDisplay(connectionStatus)

	useEffect(() => {
		if (device?.lastUpdated) {
			setLastUpdateTime(device.lastUpdated)
		}
	}, [device?.lastUpdated])

	const getRawShadow = (): NormalizedShadowData | null => {
		if (!device?.rawShadowData) return null
		return device.rawShadowData as unknown as NormalizedShadowData
	}

	const getDeviceInfo = () => {
		const rawShadow = getRawShadow()
		if (!rawShadow) return null

		let softwareVersionRaw = "N/A"
		let radarVersionsRaw: string | number[] | undefined = undefined
		let radarFwStatus: string | undefined = undefined
		let fwStatus: string | undefined = undefined

		const defaultShadow = rawShadow.default
		const radarShadow = rawShadow.radar

		if (defaultShadow?.state?.reported) {
			const r = defaultShadow.state.reported
			if (r.FW_VERSION) softwareVersionRaw = r.FW_VERSION
			if (r.RADAR_FIRMWARE !== undefined) radarVersionsRaw = r.RADAR_FIRMWARE
			if (r.RADAR_FW_STATUS) radarFwStatus = r.RADAR_FW_STATUS
			if (r.FW_STATUS) fwStatus = r.FW_STATUS
		} else if (radarShadow?.state) {
			const s = radarShadow.state
			if (s.softwareVersion) softwareVersionRaw = s.softwareVersion as string
			if (s.radarFirmwareVersions !== undefined)
				radarVersionsRaw = s.radarFirmwareVersions as string | number[]
		}

		return buildDeviceInfo(softwareVersionRaw, radarVersionsRaw, radarFwStatus, fwStatus)
	}

	const getShadowSummary = () => {
		const rawShadow = getRawShadow()
		if (!rawShadow) return null

		const summaryData: Record<string, {state: unknown; metadata: string}> = {}

		if (rawShadow.radar) {
			summaryData.radar = {
				state: rawShadow.radar.state || "No state",
				metadata: rawShadow.radar.metadata
					? Object.keys(rawShadow.radar.metadata).length + " fields"
					: "No metadata",
			}
		}

		if (rawShadow.default) {
			summaryData.default = {
				state: rawShadow.default.state || "No state",
				metadata: rawShadow.default.metadata
					? Object.keys(rawShadow.default.metadata).length + " fields"
					: "No metadata",
			}
		}

		return summaryData
	}

	if (!device) {
		return (
			<div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
				<div className="flex justify-between items-center mb-2">
					<div className="flex items-center gap-2 font-semibold text-gray-600 dark:text-gray-300">
						{isFavorite && <span className="text-yellow-400 text-xs">⭐</span>}
						Device: {deviceId}
					</div>
					<div className="flex items-center gap-2">
						<button
							onClick={() => toggleFavorite(deviceId)}
							title={isFavorite ? "Remove from favorites" : "Add to favorites"}
							className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
								isFavorite
									? "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/60"
									: "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 hover:text-yellow-700 dark:hover:text-yellow-300"
							}`}>
							{isFavorite ? "⭐" : "☆"}
						</button>
						<div
							className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${statusDisplay.className}`}>
							{statusDisplay.icon} {statusDisplay.text}
						</div>
					</div>
				</div>
				<div className="text-sm text-gray-500 dark:text-gray-400">No shadow data available</div>
				{connectionStatus?.timestamp && (
					<div className="text-xs text-gray-400 dark:text-gray-500 mt-2">
						Last seen: {new Date(connectionStatus.timestamp).toLocaleString()}
					</div>
				)}
				{isLiveMode && (
					<div className="text-xs text-blue-500 dark:text-blue-400 mt-1">
						🔄 Waiting for live data...
					</div>
				)}
			</div>
		)
	}

	const shadowSummary = getShadowSummary()
	const deviceInfo = getDeviceInfo()
	const rawShadow = getRawShadow()

	return (
		<div
			className={`p-4 rounded-lg border transition-all duration-300 ${
				device.error
					? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
					: isLiveMode
						? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
						: "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
			}`}>
			<DeviceCardHeader
				deviceId={deviceId}
				isLiveMode={isLiveMode}
				statusDisplay={statusDisplay}
				lastUpdateTime={lastUpdateTime}
				isFavorite={isFavorite}
				showShadowData={showShadowData}
				onToggleFavorite={() => toggleFavorite(deviceId)}
				onToggleShadow={() => setShowShadowData(!showShadowData)}
				onRefresh={onRefresh}
				getTimeSinceLastUpdate={() => getTimeSinceLastUpdate(lastUpdateTime)}
			/>
			{connectionStatus?.timestamp && (
				<div className="text-xs text-gray-400 dark:text-gray-500 mb-3">
					Last seen: {new Date(connectionStatus.timestamp).toLocaleString()}
				</div>
			)}
			{deviceInfo && <DeviceInfoPanel deviceInfo={deviceInfo} />}
			{device.error && (
				<div className="bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 px-3 py-2 rounded mb-4">
					<strong>Error:</strong> {device.error}
				</div>
			)}
			{shadowSummary && !showShadowData && <ShadowSummaryPanel shadowSummary={shadowSummary} />}
			{showShadowData && rawShadow && (
				<ShadowFullDataPanel rawShadow={rawShadow} deviceId={deviceId} />
			)}
			{rawShadow && (
				<div className="grid grid-cols-2 gap-4 mb-4 text-sm">
					<div>
						<span className="text-gray-500 dark:text-gray-400">Shadows available:</span>
						<div className="font-medium text-gray-900 dark:text-gray-100">
							{[rawShadow.default && "Classic", rawShadow.radar && "Radar"]
								.filter(Boolean)
								.join(", ") || "None"}
						</div>
					</div>
					<div>
						<span className="text-gray-500 dark:text-gray-400">Data size:</span>
						<div className="font-medium text-gray-900 dark:text-gray-100">
							{JSON.stringify(device.rawShadowData).length} bytes
						</div>
					</div>
				</div>
			)}
			<div className="text-xs text-gray-500 dark:text-gray-400 mt-4 border-t border-gray-200 dark:border-gray-700 pt-2 flex justify-between items-center">
				<div>Shadow updated: {device.lastUpdated.toLocaleString()}</div>
				{isLiveMode && (
					<div className="flex items-center gap-1 text-blue-500 dark:text-blue-400">
						<span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
						Live Mode
					</div>
				)}
			</div>
		</div>
	)
}

export default DeviceCard
